/**
 * AP-10 Nachtrag 2 — **Mailservice: Downloadlinks statt Anhang.**
 *
 * Die Einstellung liegt in `Tenant.settings.mail.attachmentLinks` und gilt für
 * jeden Versand über den Mailservice. Das Beleg-PDF hängt immer an; die Regel
 * betrifft die Positionsdateien (AP-32 Nachtrag 4).
 *
 * Nachtrag 3: ZIP ab einer Anzahl Dateien, Mail-Kontingent je Mandant.
 * Nachtrag 1: Versand über das eigene Microsoft-365-Postfach.
 */

export const ATTACHMENT_LINK_MODES = ['OFF', 'ABOVE_SIZE', 'ALWAYS'] as const
export type AttachmentLinkMode = (typeof ATTACHMENT_LINK_MODES)[number]

export interface AttachmentLinkSettings {
  /** OFF: alles hängt an · ABOVE_SIZE: ab `thresholdMb` als Link · ALWAYS: immer als Link */
  mode: AttachmentLinkMode
  /** Gilt nur bei ABOVE_SIZE — Summe der Positionsdateien in MB. */
  thresholdMb: number
  /** So viele Tage ist ein Link abrufbar. */
  validityDays: number
  /** Nachtrag 3: Ab so vielen Link-Dateien gibt es einen ZIP-Link statt Einzel-Links. */
  zipFromFiles: number
}

export const ATTACHMENT_LINK_THRESHOLD_MB = { min: 1, max: 35 } as const
export const ATTACHMENT_LINK_VALIDITY_DAYS = { min: 1, max: 365 } as const
export const ATTACHMENT_LINK_ZIP_FROM_FILES = { min: 2, max: 100 } as const

/** Vorgabe, solange ein Mandant nichts gespeichert hat (Nachtrag 2, 9.2-3; Nachtrag 3, 9.2-1). */
export const DEFAULT_ATTACHMENT_LINK_SETTINGS: AttachmentLinkSettings = {
  mode: 'ABOVE_SIZE',
  thresholdMb: 10,
  validityDays: 30,
  zipFromFiles: 3,
}

/** Wie eine Datei die Mail erreicht. */
export type AttachmentDelivery = 'ATTACHMENT' | 'LINK'

/** Zustand eines Downloadlinks, abgeleitet — nie gespeichert. */
export type MailFileLinkStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED'

// ── Nachtrag 3: Mail-Kontingent ──────────────────────────────────────────────

/** Mails je Mandant und Kalendermonat ohne Zusatzmenge (Auftraggeber 15.9.2026). */
export const MAIL_BASE_QUOTA_PER_MONTH = 500

/** Obergrenze der frei eingetragenen Zusatzmenge — gegen Tippfehler, nicht als Tarif. */
export const MAIL_EXTRA_QUOTA_MAX = 1_000_000

/** Stand des Kontingents in einem Monat. */
export interface MailQuotaStatus {
  /** Kalendermonat in UTC, `JJJJ-MM`. */
  month: string
  used: number
  base: number
  extra: number
  limit: number
}

// ── Nachtrag 1: Microsoft 365 ────────────────────────────────────────────────

/** OFF: niemand verbindet · OPTIONAL: jeder mit Recht darf · REQUIRED: Belege nur über das eigene Postfach */
export const MICROSOFT365_POLICIES = ['OFF', 'OPTIONAL', 'REQUIRED'] as const
export type Microsoft365Policy = (typeof MICROSOFT365_POLICIES)[number]

/** Was ein Aufrufer wählt. `SYSTEM` ist der Versand über Symphenia (Resend). */
export const MAIL_TRANSPORT_CHOICES = ['MICROSOFT_365', 'SYSTEM'] as const
export type MailTransportChoice = (typeof MAIL_TRANSPORT_CHOICES)[number]

/** Was im Versandprotokoll steht. */
export type MailTransport = 'MICROSOFT_365' | 'RESEND'

/** `Tenant.settings.mail.microsoft365` — gepflegt mit `core.mail.manage`. */
export interface Microsoft365Settings {
  policy: Microsoft365Policy
  /** Leer = jede Domain. Sonst muss die Adresse des verbundenen Kontos auf eine davon enden. */
  allowedDomains: string[]
  /** Vorwahl im Versanddialog — wirkt nur bei OPTIONAL (Nachtrag 1, 19.2-16). */
  defaultTransport: MailTransportChoice
}

/** Vorgabe für Bestands- und Neumandanten (Nachtrag 1, 19.2-8). */
export const DEFAULT_MICROSOFT365_SETTINGS: Microsoft365Settings = {
  policy: 'OFF',
  allowedDomains: [],
  defaultTransport: 'MICROSOFT_365',
}

export const MAIL_ALLOWED_DOMAINS_MAX = 20

export type MailConnectionStatus = 'OK' | 'EXPIRED'

/** Die eigene Postfachverbindung, wie das Profil sie sieht — ohne Token. */
export interface MailConnectionView {
  provider: 'MICROSOFT_365'
  address: string
  displayName: string | null
  status: MailConnectionStatus
  lastError: string | null
  connectedAt: string
  lastUsedAt: string | null
}

// ── Nachtrag 5: Exporte per E-Mail ───────────────────────────────────────────

/**
 * Woher eine Mail aus dem Exportversand stammt. Neue Exporte ergänzen hier ihren
 * Schlüssel — Versandprotokoll und „zuletzt"-Adressen hängen daran.
 */
export const MAIL_SOURCE_TYPES = [
  'EXPORT_TIME_ENTRIES',
  'EXPORT_DAILY_WORK_TIMES',
  'EXPORT_ARTICLES',
  'EXPORT_DOCUMENTS',
  'EXPORT_WORK_COCKPIT',
  'EXPORT_WEEK_ASSIGNMENT',
  'EXPORT_MATERIAL_CERTIFICATES',
  'EXPORT_PURCHASING',
  'EXPORT_PROJECTS',
  'EXPORT_PROJECT_TASKS',
  'EXPORT_PROJECT_BILLING',
  'EXPORT_PROJECT_ACTUALS',
  /** Die frühere Arbeitskarte — entfallen mit AP-35 Nachtrag 9; bleibt für das Versandprotokoll. */
  'EXPORT_WORK_CARD',
  'EXPORT_WORK_ORDER',
  'EXPORT_PROJECT_BOM',
  /** AP-22-B: Arbeitszeitmodelle der Lohnverrechnung. */
  'EXPORT_PAYROLL_WORK_SCHEDULES',
  /** AP-22-E: Monatsauswertung der Lohnverrechnung. */
  'EXPORT_PAYROLL_MONTHLY',
  /** AP-22 Teil 2, J: Monatsübersicht — Stand je Mitarbeiter. */
  'EXPORT_PAYROLL_MONTHS',
  /** AP-22 Teil 2, F: die BMD-Datei. */
  'EXPORT_PAYROLL_BMD',
  /** AP-35 Phase J — Statistik eines LV im Projekt. */
  'EXPORT_PROJECT_LV',
  'EXPORT_CALCULATIONS',
  /** AP-34 — Rechnungsliste und offene Posten der Eingangsrechnungen. */
  'EXPORT_INCOMING_INVOICES',
  /** AP-34 13.3 — der Exportstapel für die Buchhaltung. */
  'EXPORT_INCOMING_BATCH',
  /** AP-34 Nachtrag 2, 3.4 — Auswertung der Gemeinkosten. */
  'EXPORT_INCOMING_OVERHEAD',
  /** AP-37 Phase G — Ausdruck einer Kalkulation. */
  'EXPORT_CALCULATION',
  /** AP-37 Detailblatt O — Material aller Kalkulationen im Cockpit „Materialanfragen". */
  'EXPORT_CALCULATION_MATERIAL',
  /** AP-37 Detailblatt K — eine gespeicherte Ausgabe (Angebots-LV, Beilagen). */
  'CALCULATION_LV_OUTPUT',
  /** AP-37 Detailblatt K 5 — Druckbild des Angebots-LV (für sich, nicht gespeichert). */
  'EXPORT_CALCULATION_LV_PRINT',
  /** AP-37 Detailblatt K 6 — Formblatt K3 oder K7. */
  'EXPORT_CALCULATION_FORM',
  'MONTHLY_REPORT',
  'MONTHLY_REPORT_SUBMISSION',
  /** AP-46 10.2 — Kopie eines Baustellendokuments an den Kunden (`POST /site-docs/:id/pdf/mail`). */
  'SITE_DOCUMENT',
  /** AP-46 11.2 — die Liste der Baustellendokumente. */
  'EXPORT_SITE_DOCUMENTS',
  /** AP-46 22. — Bestätigung per Link: die Bitte an den Kunden samt PDF und Erinnerungen. */
  'SITE_DOCUMENT_CONFIRMATION',
  /** AP-27 19. — die Regiezeilen für ein fremdes ERP. */
  'EXPORT_SITE_REPORT_LINES',
  /** AP-47 6.2 — die Mängelliste der Montageprotokolle. */
  'EXPORT_SITE_ASSEMBLY_DEFECTS',
  /** AP-48 6.3 (RN-6) — Sammel-Gegenzeichnung per Link: die Bitte an die Bauaufsicht samt Verzeichnis und Erinnerungen. */
  'SITE_DOCUMENT_COUNTERSIGN',
  /** AP-48 7. — das Bautagebuch eines Zeitraums als ein PDF. */
  'EXPORT_SITE_DIARY_BOOK',
  /** AP-48 7. — die Behinderungsliste. */
  'EXPORT_SITE_DIARY_HINDRANCES',
  /** AP-49 7.1 — Protokoll einer Projektbesprechung an den Verteiler. */
  'SITE_DOCUMENT_DISTRIBUTION',
  /** AP-49 8. — Liste der offenen Punkte aus Besprechungen. */
  'EXPORT_SITE_MEETING_ITEMS',
] as const
export type MailSourceType = (typeof MAIL_SOURCE_TYPES)[number]

/** Grenzen des Exportversands — dieselben wie im Belegdialog. */
export const MAIL_EXPORT_LIMITS = {
  recipients: 20,
  subject: 250,
  message: 10_000,
  signature: 1_000,
  signatureLines: 12,
  /** Eigene Dateien je Mail (Nachtrag 5, 3a) — zusätzlich zum Export. */
  files: 10,
} as const

/**
 * Nachtrag 5, 3a — Endungen, die als eigene Datei mitgehen dürfen. Der Server prüft
 * zusätzlich den Inhalt (`mail-anhang.ts`); die Liste hier speist den Dateiauswähler.
 */
export const MAIL_ATTACHMENT_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'docx', 'xlsx', 'pptx', 'zip', 'doc', 'xls', 'dwg', 'dxf', 'csv', 'txt'] as const

/** So viele verschiedene Adressen schlägt der Dialog unter „zuletzt" vor. */
export const MAIL_RECENT_RECIPIENTS = 5

/** Was der Versanddialog zum Öffnen braucht (`GET /mail/compose`). */
export interface MailComposeDraft {
  transports: {
    /** Nur, wenn die Richtlinie es erlaubt, der Benutzer das Recht hat und verbunden ist — wie im Belegdialog. */
    microsoft365: { status: MailConnectionStatus; address: string | null } | null
    system: { address: string } | null
  }
  /** `null` = Pflicht zum eigenen Postfach ohne gültige Verbindung — Senden gesperrt. */
  defaultTransport: MailTransportChoice | null
  /** Hinweis, dass ein eigenes Postfach verbunden werden könnte. */
  connectHint: boolean
  replyTo: string
  /** Die eigene Signatur, sonst die Vorgabe aus Profil und Branding. */
  signature: string
  recentRecipients: string[]
}

/** Rumpf-Teil `mail` jedes `…/mail`-Endpunkts. */
export interface MailExportPayload {
  to: string[]
  copyToSelf?: boolean
  subject: string
  message: string
  transport?: MailTransportChoice
}

/** Antwort eines erfolgreichen Versands. */
export interface MailExportResult {
  transport: MailTransport
  senderAddress: string
  recipients: string[]
  fileName: string
}

/** Eine Zeile im Versandprotokoll (`GET /mail/dispatches`). */
export interface MailDispatchRow {
  id: string
  sentAt: string
  sentBy: { id: string; name: string } | null
  sourceType: MailSourceType
  fileName: string
  format: string
  /** Summe aus Export und eigenen Dateien. */
  sizeBytes: number
  /** Nachtrag 5, 3a: eigene Dateien neben dem Export. */
  extraFiles: { fileName: string; sizeBytes: number }[]
  recipients: string[]
  copyToSelf: boolean
  subject: string
  transport: MailTransport
  senderAddress: string | null
  error: string | null
}

/** Die eigene Signatur (`GET /mail/signature`). */
export interface MailSignatureView {
  /** Gespeichert — leer heißt: die Vorgabe gilt. */
  signature: string | null
  /** Die Vorgabe aus Profil und Branding, zum Anzeigen und Übernehmen. */
  defaultSignature: string
  /** AP-36 8.1: Stand der Profilzeile — `null`, solange es sie nicht gibt. */
  updatedAt?: Date | string | null
}
